/**
 * Outil « Tableau de numération » (Boîte à outils, 6e → 4e).
 *
 * Un nombre décimal rangé dans le tableau de numération ; × 10, × 100,
 * × 1 000, ÷ 10… font GLISSER les chiffres d'autant de rangs pendant que la
 * virgule reste fixe — « multiplier par 10, ce n'est pas ajouter un zéro ».
 * Décomposition (3 × 10 + 4 × 1 + 7 × 0,1) et historique des opérations.
 * Calculs : `place-value.ts`.
 */

import { useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, NumberField, SECONDARY_BUTTON } from '../../ui/controls';
import {
  canShift,
  COLUMNS,
  decomposition,
  describeDigits,
  digitsOf,
  displayedCells,
  groupThousands,
  OPERATIONS,
  shiftedText,
} from './place-value';

const colIndex = (exp: number) => COLUMNS.findIndex((c) => c.exp === exp);
const INTEGER_COLUMNS = COLUMNS.filter((c) => c.exp >= 0).length;

export function PlaceValueTool({ onSend, sendCost }: ToolProps) {
  const [value, setValue] = useState(3.47);
  const [shift, setShift] = useState(0);
  const [history, setHistory] = useState<string[]>([]);
  const digits = digitsOf(value);
  const text = shiftedText(digits, shift);
  const shown = groupThousands(text);
  const cells = displayedCells(digits, shift);
  const blocked = OPERATIONS.filter((op) => !canShift(digits, shift + op.shift));

  const apply = (op: (typeof OPERATIONS)[number]) => {
    const next = shift + op.shift;
    setHistory((h) =>
      [
        ...h,
        `${groupThousands(shiftedText(digits, shift))} ${op.label} = ${groupThousands(shiftedText(digits, next))}`,
      ].slice(-4),
    );
    setShift(next);
  };

  return (
    <div className="space-y-4">
      <NumberField
        label="Nombre"
        value={value}
        onChange={(v) => {
          setValue(v);
          setShift(0);
          setHistory([]);
        }}
        min={0}
        max={99999}
        decimals={4}
        step={1}
      />

      <div>
        <div
          className="border-ink-100 overflow-hidden rounded-2xl border"
          role="img"
          aria-label={`Tableau de numération : ${shown}, soit ${describeDigits(digits, shift)}.`}
          data-testid="pv-table"
        >
          <div className="grid grid-cols-10 text-xs font-semibold">
            <div
              className="bg-ink-50 text-ink-600 py-1 text-center"
              style={{ gridColumn: `span ${INTEGER_COLUMNS}` }}
            >
              partie entière
            </div>
            <div
              className="bg-primary-50 text-ink-700 py-1 text-center"
              style={{ gridColumn: `span ${COLUMNS.length - INTEGER_COLUMNS}` }}
            >
              partie décimale
            </div>
          </div>
          <div className="bg-ink-50 border-ink-100 grid grid-cols-10 border-t">
            {COLUMNS.map((c) => (
              <div
                key={c.exp}
                title={c.name}
                className={cn(
                  'text-ink-700 py-1 text-center text-xs font-bold',
                  c.exp < 0 && 'bg-primary-50',
                )}
              >
                {c.short}
              </div>
            ))}
          </div>
          <div className="relative h-14">
            {/* séparations des colonnes */}
            {COLUMNS.map((c, i) => (
              <div
                key={c.exp}
                className={cn(
                  'border-ink-100 absolute inset-y-0 border-l',
                  c.exp < 0 && 'bg-primary-50/40',
                )}
                style={{ left: `${i * 10}%`, width: '10%' }}
              />
            ))}
            {cells.map((cell) => (
              <div
                key={cell.key}
                className={cn(
                  'absolute inset-y-0 flex items-center justify-center text-2xl font-bold tabular-nums transition-[left] duration-500 ease-out motion-reduce:transition-none',
                  cell.key.startsWith('z') ? 'text-ink-400' : 'text-ink-900',
                )}
                style={{ left: `${colIndex(cell.exp) * 10}%`, width: '10%' }}
              >
                {cell.digit}
              </div>
            ))}
            {/* la virgule ne bouge pas */}
            <div
              className="text-error-500 absolute bottom-1 text-3xl font-black leading-none"
              style={{ left: `calc(${INTEGER_COLUMNS * 10}% - 5px)` }}
            >
              ,
            </div>
          </div>
        </div>
        <p className="text-ink-500 mt-1 text-xs">
          d, c, m, dm : dixièmes, centièmes, millièmes, dix-millièmes.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2" role="group" aria-label="Opérations">
        {OPERATIONS.map((op) => (
          <button
            key={op.label}
            type="button"
            className={cn(SECONDARY_BUTTON, 'px-2 disabled:opacity-40')}
            disabled={blocked.includes(op)}
            onClick={() => apply(op)}
          >
            {op.label}
          </button>
        ))}
      </div>
      {blocked.length > 0 && (
        <p className="text-ink-500 text-sm" data-testid="pv-blocked">
          {blocked.map((op) => op.label).join(', ')} : un chiffre sortirait du tableau.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <p
          className="text-ink-900 min-w-0 flex-1 text-2xl font-bold tabular-nums"
          data-testid="pv-value"
        >
          {shown}
        </p>
        {onSend && (
          <SendToSheetButton
            label={text}
            cost={sendCost}
            onClick={() => onSend({ atoms: [text] })}
          />
        )}
      </div>
      <p className="text-ink-700 text-sm" data-testid="pv-decomposition">
        {shown} = {decomposition(digits, shift)}
      </p>
      {history.length > 0 && (
        <ol
          className="text-ink-600 space-y-0.5 text-sm"
          aria-label="Opérations faites"
          data-testid="pv-history"
        >
          {history.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ol>
      )}
      <Note live={false}>
        Multiplier par 10, c’est décaler chaque chiffre d’un rang vers la gauche : la virgule, elle,
        ne bouge pas. « Ajouter un zéro » ne marche pas pour 3,47 : 3,470 vaut toujours 3,47.
      </Note>
    </div>
  );
}
